/**
 * Mock users service.
 * Mirrors the real users.service.js API with in-memory data.
 */
import { MOCK_USERS, getMockUserById, getMockUserByUsername } from '../../data/mock/mock-users.data';
import { mockDelay, mockLog } from './mock-delay';

// In-memory state
let users = [...MOCK_USERS];

export const createUserProfile = async (userId, profileData) => {
  mockLog('UsersService', 'createUserProfile', userId);
  await mockDelay();
  const userDoc = {
    id: userId,
    displayName: profileData.displayName || '',
    username: profileData.username || '',
    email: profileData.email || '',
    bio: profileData.bio || '',
    avatarUrl: profileData.avatarUrl || '',
    birthdate: profileData.birthdate || null,
    gender: profileData.gender || '',
    interests: profileData.interests || [],
    onboardingCompleted: false,
    createdAt: new Date().toISOString(),
    followersCount: 0,
    followingCount: 0,
    postsCount: 0,
  };
  users.push(userDoc);
  return userDoc;
};

export const getUserProfile = async (userId) => {
  mockLog('UsersService', 'getUserProfile', userId);
  await mockDelay();
  return getMockUserById(userId) || users.find((u) => u.id === userId) || null;
};

export const updateUserProfile = async (userId, updates) => {
  mockLog('UsersService', 'updateUserProfile', { userId, updates });
  await mockDelay();
  const idx = users.findIndex((u) => u.id === userId);
  if (idx !== -1) {
    users[idx] = { ...users[idx], ...updates, updatedAt: new Date().toISOString() };
    return users[idx];
  }
  return null;
};

export const isUsernameAvailable = async (username) => {
  mockLog('UsersService', 'isUsernameAvailable', username);
  await mockDelay(100, 300);
  return !users.some((u) => u.username === username.toLowerCase());
};

export const getUserByUsername = async (username) => {
  mockLog('UsersService', 'getUserByUsername', username);
  await mockDelay();
  return getMockUserByUsername(username) || users.find((u) => u.username === username.toLowerCase()) || null;
};

export const searchUsers = async (searchQuery, resultLimit = 10) => {
  mockLog('UsersService', 'searchUsers', searchQuery);
  await mockDelay();
  const q = searchQuery.toLowerCase().trim();
  if (!q) return [];
  return users
    .filter((u) => u.username.includes(q) || u.displayName.toLowerCase().includes(q))
    .slice(0, resultLimit);
};

// Online status tracking (in-memory)
const onlineUsers = new Set(['mock_user_002', 'mock_user_005']); // Sarah and Jake are online

export const setOnlineStatus = async (userId, isOnline) => {
  mockLog('UsersService', 'setOnlineStatus', { userId, isOnline });
  if (isOnline) {
    onlineUsers.add(userId);
  } else {
    onlineUsers.delete(userId);
  }
};

export const onUserStatusChange = (userId, callback) => {
  mockLog('UsersService', 'onUserStatusChange', userId);
  setTimeout(() => {
    callback({
      isOnline: onlineUsers.has(userId),
      lastSeen: onlineUsers.has(userId) ? new Date().toISOString() : new Date(Date.now() - 300000).toISOString(),
    });
  }, 100);
  return () => {};
};
